import { MouseEvent } from 'react';
import { Box, Button, Typography, Stack, InputAdornment, Alert } from '@mui/material';
import { Control, FieldErrorsImpl, UseFormSetValue } from 'react-hook-form';
import { TextField } from '@/components/forms/inputs';
import { usePhrase } from '@/hooks';
import { WALLET_REQUEST_PHRASES, WALLET_PHRASES } from '@/consts/phrases/walletPhrases';
import { walletFormModel } from '@/components/forms/forms/wallet/walletFormModel';
import { WalletPaymentSteps } from '@/types/enums/forms/wallet/walletPaymentSteps';
import { NonTaxable } from '@/components/blocks/wallet/NonTaxable/NonTaxable';
import { formatDistance, subDays } from 'date-fns';

import { JSXFromString } from '@/utils';
import { IWalletStartPaymentFormDetails } from '@/types/types';

interface WalletPaymentAmountProps {
    control: Control;
    errors?: Partial<FieldErrorsImpl<IWalletStartPaymentFormDetails>>;
    taxableSum: number;
    currencyCode: string;
    currencySymbol?: string;
    currencyPosition?: string;
    nonTaxableSum?: number;
    existingPaymentDt?: string;
    setValue: UseFormSetValue<Partial<IWalletStartPaymentFormDetails>>;
    handleOpenRequestDialog: (val?: number, field?: keyof IWalletStartPaymentFormDetails) => void;
    isSubmitting?: boolean;
    paymentInfoExist?: boolean;
}

export const WalletPaymentAmount = ({
    control,
    errors,
    taxableSum,
    currencyCode,
    currencySymbol,
    currencyPosition,
    nonTaxableSum,
    existingPaymentDt,
    setValue,
    handleOpenRequestDialog,
    isSubmitting = false,
    paymentInfoExist = false,
}: WalletPaymentAmountProps) => {
    const { t } = usePhrase();
    const { amount: amountModel } = walletFormModel[WalletPaymentSteps.PaymentStart];
    const inputCurrencyPosition = currencyPosition === 'left' ? 'start' : 'end';

    const symbol = currencySymbol ? JSXFromString(currencySymbol) : currencyCode;

    const getSumText = (sum: string) => {
        if (!currencySymbol && !currencyPosition) return sum + ' ' + currencyCode;
        return currencyPosition === 'left' ? currencySymbol + sum : sum + currencySymbol;
    };

    const taxableSumLink = `<a href="#!" class="internal-link">${getSumText(
        taxableSum.toString(),
    )}</a>`;
    const nonTaxableSumLink = nonTaxableSum
        ? `<a href="#!" class="internal-link">${getSumText(
              nonTaxableSum.toString(),
          )}</a> <span class="non-taxable-badge">${t(WALLET_PHRASES.NON_TAXABLE, {
              asString: true,
          })}</span>`
        : '';

    const handleSumChange = (evt: MouseEvent<HTMLDivElement>) => {
        evt.preventDefault();
        const target = evt.target as Element;
        if (target.classList.contains('internal-link')) {
            const targetValue = target?.textContent
                ? parseInt(target.textContent.replace(symbol.toString(), ''))
                : null;
            if (targetValue) setValue(amountModel.name, targetValue);
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h5" sx={{ mb: 1 }}>
                {t(WALLET_REQUEST_PHRASES.ENTER_AMOUNT_TITLE)}
            </Typography>
            <Box sx={{ mb: 4 }}>
                <Alert icon={false} severity="info" sx={{ py: 0, px: 2 }}>
                    <Typography variant="subtitle2" onClick={handleSumChange}>
                        {JSXFromString(
                            t(WALLET_REQUEST_PHRASES.REQUEST_SUM_DESCRIPTION, {
                                replaceTextVars: [taxableSumLink],
                            }),
                        )}
                        {nonTaxableSumLink && (
                            <>
                                {' '}
                                (
                                {JSXFromString(
                                    t(WALLET_REQUEST_PHRASES.REQUEST_SUM_NON_TAXABLE_DESCRIPTION, {
                                        replaceTextVars: [nonTaxableSumLink],
                                    }),
                                )}
                                )
                            </>
                        )}
                    </Typography>
                </Alert>
            </Box>
            <Stack sx={{ flexDirection: ['column', 'row'], flexWrap: 'wrap', gap: [1, 2] }}>
                <TextField
                    control={control}
                    name={amountModel.name}
                    label={amountModel.label}
                    type="number"
                    required
                    error={errors?.amount}
                    data-testid="amount-input"
                    slotProps={{
                        input: {
                            ...(inputCurrencyPosition === 'start'
                                ? {
                                      startAdornment: (
                                          <InputAdornment position="start">{symbol}</InputAdornment>
                                      ),
                                  }
                                : {
                                      endAdornment: (
                                          <InputAdornment position="end">{symbol}</InputAdornment>
                                      ),
                                  }),
                        },
                    }}
                />
                <Button
                    id="wallet-request-submit"
                    type="submit"
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    variant="contained"
                    sx={{ height: '40px' }}
                >
                    {t(WALLET_REQUEST_PHRASES.REQUEST_BUTTON)}
                </Button>
            </Stack>
            {existingPaymentDt && paymentInfoExist && (
                <Box sx={{ mt: 2 }}>
                    <Alert icon={false} severity="warning" sx={{ py: 0, px: 2 }}>
                        <Typography variant="subtitle2">
                            {t(WALLET_REQUEST_PHRASES.TO_EXISTING_PAYMENT_DESCRIPTION, {
                                replaceTextVars: [
                                    formatDistance(
                                        subDays(new Date(existingPaymentDt), 0),
                                        new Date(),
                                        {
                                            addSuffix: true,
                                        },
                                    ),
                                ],
                            })}{' '}
                            <a
                                href="#!"
                                className="internal-link"
                                onClick={(evt) => {
                                    evt.preventDefault();
                                    handleOpenRequestDialog();
                                }}
                            >
                                {t(WALLET_REQUEST_PHRASES.SEPARATE_PAYMENT_QUESTION).toString()}
                            </a>
                        </Typography>
                    </Alert>
                </Box>
            )}
            {nonTaxableSumLink && (
                <Box sx={{ mt: [4, 2] }}>
                    <Typography variant="caption">
                        <Box sx={{ display: 'inline-flex', mr: 0.75 }}>
                            <NonTaxable />
                        </Box>
                        {t(WALLET_REQUEST_PHRASES.NON_TAXABLE_DESCRIPTION)}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
